import { forwardRef, useEffect, useState } from "react";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { PaymentHistory } from "@/types/payment";

interface ShopSettings {
  shopName: string;
  shopAddress: string;
  phone1: string;
  phone2: string;
  receiptNote: string;
  logo: string;
  theme: "light" | "dark" | "system";
  primaryColor: string;
}

const DEFAULT_SETTINGS: ShopSettings = {
  shopName: "پێشانگای قەندیل",
  shopAddress: "",
  phone1: "",
  phone2: "",
  receiptNote: "",
  logo: "",
  theme: "system",
  primaryColor: "default",
};

interface PaymentInvoiceProps {
  customer: Customer;
  payments: PaymentHistory[];
  products: Product[];
  invoiceNumber: number;
  paymentDate: Date;
}

export const PaymentInvoice = forwardRef<HTMLDivElement, PaymentInvoiceProps>(
  function PaymentInvoice({ customer, payments, products, invoiceNumber, paymentDate }, ref) {
    const [settings, setSettings] = useState<ShopSettings>(DEFAULT_SETTINGS);

    useEffect(() => {
      const stored = localStorage.getItem("qandil-settings");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setSettings({ ...DEFAULT_SETTINGS, ...parsed });
        } catch {
          setSettings(DEFAULT_SETTINGS);
        }
      }
    }, []);

    const formatDate = (date: Date | string) => {
      const d = typeof date === "string" ? new Date(date) : date;
      const day = d.getDate().toString().padStart(2, "0");
      const month = (d.getMonth() + 1).toString().padStart(2, "0");
      const year = d.getFullYear();
      return `${year}/${month}/${day}`;
    };

    // گروپکردنی پارەدانەکان بە دراو
    const iqdPayments = payments.filter((p) => p.currency === "IQD");
    const usdPayments = payments.filter((p) => p.currency === "USD");

    const totalIQD = iqdPayments.reduce((sum, p) => sum + p.amount, 0);
    const totalUSD = usdPayments.reduce((sum, p) => sum + p.amount, 0);

    // کۆی ماوە بۆ هەر دراوێک
    const customerProducts = products.filter((p) => p.customerId === customer.id);
    
    // حیسابکردنی ماوە دوای ئەم پارەدانە - لە زانیاری پارەدان وەردەگیرێت
    const getRemainingAfterPayment = (productId: string) => {
      const payment = payments.find((pay) => pay.productId === productId);
      // ئەگەر remainingAfterPayment لە پارەدان هەبوو، بەکاری بهێنە
      if (payment && payment.remainingAfterPayment !== undefined) {
        return Math.max(0, payment.remainingAfterPayment);
      }
      // بۆ پارەدانە کۆنەکان کە remainingAfterPayment نییان
      const product = customerProducts.find((p) => p.id === productId);
      if (!product) return 0;
      const paidNow = payment ? payment.amount : 0;
      return Math.max(0, product.remainingAmount - paidNow);
    };

    const totalRemainingIQD = customerProducts
      .filter((p) => p.currency === "IQD")
      .reduce((sum, p) => sum + getRemainingAfterPayment(p.id), 0);
    
    const totalRemainingUSD = customerProducts
      .filter((p) => p.currency === "USD")
      .reduce((sum, p) => sum + getRemainingAfterPayment(p.id), 0);

    return (
      <div ref={ref} className="payment-invoice-container">
        <div className="invoice-wrapper">
          <InvoiceCopy
            settings={settings}
            customer={customer}
            payments={payments}
            products={products}
            invoiceNumber={invoiceNumber}
            paymentDate={paymentDate}
            formatDate={formatDate}
            totalIQD={totalIQD}
            totalUSD={totalUSD}
            totalRemainingIQD={totalRemainingIQD}
            totalRemainingUSD={totalRemainingUSD}
            getRemainingAfterPayment={getRemainingAfterPayment}
          />
          
          <InvoiceCopy
            settings={settings}
            customer={customer}
            payments={payments}
            products={products}
            invoiceNumber={invoiceNumber}
            paymentDate={paymentDate}
            formatDate={formatDate}
            totalIQD={totalIQD}
            totalUSD={totalUSD}
            totalRemainingIQD={totalRemainingIQD}
            totalRemainingUSD={totalRemainingUSD}
            getRemainingAfterPayment={getRemainingAfterPayment}
          />
        </div>

        <style>{`
          .payment-invoice-container {
            width: 100%;
            background: white;
            font-family: 'Noto Kufi Arabic', 'Segoe UI', Tahoma, sans-serif;
            direction: rtl;
          }
          
          .invoice-wrapper {
            display: flex;
            gap: 10px;
            padding: 5px;
          }
          
          .invoice-copy {
            flex: 1;
            border: 1px solid #d1d5db;
            padding: 10px;
            font-size: 9px;
            background: white;
            height: 140mm;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
          }
          
          .invoice-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding-bottom: 6px;
            border-bottom: 2px solid #dc2626;
            margin-bottom: 6px;
          }
          
          .shop-info {
            text-align: right;
          }
          
          .shop-name {
            font-size: 14px;
            font-weight: bold;
            color: #dc2626;
            margin-bottom: 1px;
          }
          
          .shop-subtitle {
            font-size: 9px;
            color: #6b7280;
          }
          
          .shop-address {
            font-size: 8px;
            color: #374151;
            margin-top: 2px;
          }
          
          .shop-phones {
            font-size: 8px;
            color: #374151;
            margin-top: 2px;
            direction: ltr;
            text-align: right;
          }
          
          .logo-container {
            width: 45px;
            height: 45px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .logo-container img {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
          }
          
          .logo-placeholder {
            width: 45px;
            height: 45px;
            background: #dc2626;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: 18px;
          }
          
          .invoice-meta {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            border-bottom: 1px solid #e5e7eb;
            margin-bottom: 6px;
            font-size: 8px;
          }
          
          .meta-item {
            display: flex;
            gap: 3px;
          }
          
          .meta-label {
            color: #6b7280;
          }
          
          .meta-value {
            font-weight: 600;
          }
          
          .section-title {
            font-size: 9px;
            font-weight: bold;
            color: #dc2626;
            margin-bottom: 4px;
            padding-bottom: 2px;
            border-bottom: 1px dashed #fecaca;
          }
          
          .customer-info {
            margin-bottom: 6px;
          }
          
          .customer-row {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
          }
          
          .customer-item {
            display: flex;
            gap: 3px;
            font-size: 8px;
          }
          
          .customer-label {
            color: #6b7280;
          }
          
          .customer-value {
            font-weight: 500;
          }
          
          .payments-section {
            flex: 1;
            margin-bottom: 6px;
          }
          
          .payments-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 7px;
          }
          
          .payments-table th {
            background: #f3f4f6;
            padding: 3px 2px;
            text-align: right;
            font-weight: 600;
            border: 1px solid #d1d5db;
            font-size: 7px;
          }
          
          .payments-table td {
            padding: 3px 2px;
            border: 1px solid #d1d5db;
            font-size: 7px;
          }
          
          .payments-table .amount {
            text-align: left;
            direction: ltr;
          }
          
          .payments-table .date {
            text-align: center;
            direction: ltr;
          }
          
          .payments-table .installment {
            text-align: center;
          }
          
          .payments-table .paid-amount {
            color: #16a34a;
            font-weight: 600;
          }
          
          .payments-table .remaining-amount {
            color: #dc2626;
          }
          
          .totals-section {
            display: flex;
            justify-content: center;
            gap: 12px;
            padding-top: 6px;
            border-top: 1px solid #e5e7eb;
            margin-top: auto;
          }
          
          .total-box {
            text-align: center;
            padding: 4px 10px;
            border-radius: 4px;
            min-width: 70px;
          }
          
          .total-box.paid {
            background: #dcfce7;
            border: 1px solid #86efac;
          }
          
          .total-box.remaining {
            background: #fee2e2;
            border: 1px solid #fca5a5;
          }
          
          .total-label {
            font-size: 7px;
            color: #6b7280;
            margin-bottom: 1px;
          }
          
          .total-value {
            font-size: 11px;
            font-weight: bold;
          }
          
          .total-value.green {
            color: #16a34a;
          }
          
          .total-value.red {
            color: #dc2626;
          }
          
          .total-currency {
            font-size: 7px;
            color: #6b7280;
          }
          
          .receipt-note {
            padding-top: 4px;
            border-top: 1px dashed #e5e7eb;
            text-align: center;
            font-size: 8px;
            color: #6b7280;
            margin-top: 6px;
          }
          
          @media print {
            @page {
              size: A4 landscape;
              margin: 5mm;
            }
            
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .payment-invoice-container {
              width: 100%;
            }
            
            .invoice-wrapper {
              display: flex;
              gap: 10px;
            }
            
            .invoice-copy {
              flex: 1;
              page-break-inside: avoid;
              height: 140mm;
            }
          }
        `}</style>
      </div>
    );
  }
);


// کۆمپۆنێنتی پسوڵەی تاک
interface InvoiceCopyProps {
  settings: ShopSettings;
  customer: Customer;
  payments: PaymentHistory[];
  products: Product[];
  invoiceNumber: number;
  paymentDate: Date;
  formatDate: (date: Date | string) => string;
  totalIQD: number;
  totalUSD: number;
  totalRemainingIQD: number;
  totalRemainingUSD: number;
  getRemainingAfterPayment: (productId: string) => number;
}

function InvoiceCopy({
  settings,
  customer,
  payments,
  products,
  invoiceNumber,
  paymentDate,
  formatDate,
  totalIQD,
  totalUSD,
  totalRemainingIQD,
  totalRemainingUSD,
  getRemainingAfterPayment,
}: InvoiceCopyProps) {
  const customerProducts = products.filter((p) => p.customerId === customer.id);

  return (
    <div className="invoice-copy">
      {/* سەرپەڕە */}
      <div className="invoice-header">
        <div className="shop-info">
          <div className="shop-name">{settings.shopName}</div>
          <div className="shop-subtitle">وەصڵی پارەدان</div>
          {settings.shopAddress && (
            <div className="shop-address">ناونیشان: {settings.shopAddress}</div>
          )}
          {(settings.phone1 || settings.phone2) && (
            <div className="shop-phones">
              {settings.phone1 && <span>{settings.phone1}</span>}
              {settings.phone1 && settings.phone2 && <span> - </span>}
              {settings.phone2 && <span>{settings.phone2}</span>}
            </div>
          )}
        </div>
        <div className="logo-container">
          {settings.logo ? (
            <img src={settings.logo} alt="Logo" />
          ) : (
            <div className="logo-placeholder">ق</div>
          )}
        </div>
      </div>

      {/* زانیاری وەصڵ */}
      <div className="invoice-meta">
        <div className="meta-item">
          <span className="meta-label">بەرواری پارەدان:</span>
          <span className="meta-value">{formatDate(paymentDate)}</span>
        </div>
        <div className="meta-item">
          <span className="meta-label">کۆدی کڕیار:</span>
          <span className="meta-value">{customer.customerCode}</span>
        </div>
        <div className="meta-item">
          <span className="meta-label">ژمارەی وەصڵ:</span>
          <span className="meta-value">{invoiceNumber}</span>
        </div>
      </div>

      {/* زانیاری کڕیار - تەنها ناو و مۆبایل */}
      <div className="customer-info">
        <div className="section-title">زانیاری کڕیار</div>
        <div className="customer-row">
          <div className="customer-item">
            <span className="customer-label">ناو:</span>
            <span className="customer-value">{customer.fullName}</span>
          </div>
          <div className="customer-item">
            <span className="customer-label">مۆبایل:</span>
            <span className="customer-value" dir="ltr">{customer.phone1}</span>
          </div>
        </div>
      </div>

      {/* وردەکاری پارەدان */}
      <div className="payments-section">
        <div className="section-title">وردەکاری پارەدان</div>
        <table className="payments-table">
          <thead>
            <tr>
              <th>ناوی کاڵا</th>
              <th>بەرواری کڕین</th>
              <th>جار</th>
              <th>نرخ</th>
              <th>بڕی دراو</th>
              <th>پارەی دراو</th>
              <th>ماوە</th>
              <th>بەرواری شایستە</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment) => {
              const product = customerProducts.find((p) => p.id === payment.productId);
              if (!product) return null;
              
              // حیسابکردنی بەرواری شایستە
              const purchaseDate = new Date(product.date);
              const dueDate = new Date(purchaseDate);
              dueDate.setDate(dueDate.getDate() + (payment.installmentNumber * 30));
              
              const remainingAfterPayment = getRemainingAfterPayment(product.id);
              
              return (
                <tr key={payment.id}>
                  <td>{payment.productName}</td>
                  <td className="date">{product.date}</td>
                  <td className="installment">{payment.installmentNumber}</td>
                  <td className="amount">
                    {payment.currency === "USD" && "$"}
                    {product.salePrice.toLocaleString()}
                    {payment.currency === "IQD" && " د.ع"}
                  </td>
                  <td className="amount">
                    {payment.currency === "USD" && "$"}
                    {product.monthlyInstallment.toLocaleString()}
                    {payment.currency === "IQD" && " د.ع"}
                  </td>
                  <td className="amount paid-amount">
                    {payment.currency === "USD" && "$"}
                    {payment.amount.toLocaleString()}
                    {payment.currency === "IQD" && " د.ع"}
                  </td>
                  <td className="amount remaining-amount">
                    {payment.currency === "USD" && "$"}
                    {remainingAfterPayment.toLocaleString()}
                    {payment.currency === "IQD" && " د.ع"}
                  </td>
                  <td className="date">{formatDate(dueDate)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* کۆی گشتی */}
      <div className="totals-section">
        {totalIQD > 0 && (
          <>
            <div className="total-box paid">
              <div className="total-label">کۆی پارەی دراو</div>
              <div className="total-value green">{totalIQD.toLocaleString()}</div>
              <div className="total-currency">د.ع</div>
            </div>
            <div className="total-box remaining">
              <div className="total-label">کۆی ماوە</div>
              <div className="total-value red">{totalRemainingIQD.toLocaleString()}</div>
              <div className="total-currency">د.ع</div>
            </div>
          </>
        )}
        {totalUSD > 0 && (
          <>
            <div className="total-box paid">
              <div className="total-label">کۆی پارەی دراو</div>
              <div className="total-value green">${totalUSD.toLocaleString()}</div>
            </div>
            <div className="total-box remaining">
              <div className="total-label">کۆی ماوە</div>
              <div className="total-value red">${totalRemainingUSD.toLocaleString()}</div>
            </div>
          </>
        )}
      </div>

      {/* تێبینی */}
      <div className="receipt-note">
        {settings.receiptNote || "سوپاس بۆ متمانەکەتان"}
      </div>
    </div>
  );
}

export default PaymentInvoice;
